import React, { FC, lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { auth } from "./firebase/config";
import cartDB from "./utils/indexedDB/cartDB";
import offlineDB from "./utils/indexedDB/offlineDB";
import customerDB from "./utils/indexedDB/customerDB";
import printJobQueue from "./utils/indexedDB/printJobQueue";
import masterDataCache from "./utils/offlineStorage/masterDataCache";
import jobProcessor from "./utils/printing/jobProcessor";
import connectionMonitor from "./utils/connectionMonitor";
import CustomTitleBar from "./components/CustomTitleBar";
import OfflineBanner from "./components/ui/OfflineBanner";
import { useOEM } from "./components/contexts/OEMContext";
import { ColorModeSync } from "./components/navigations/AppearanceControls";
import { isTauriMode, isWebMode } from "./utils/environmentDetector";
import { initDesktopShell } from "./utils/desktop/desktopShell";
import { applySavedUiScale } from "./utils/uiScale";
import { watchPermanentTime } from "./utils/permanentTime";
import { watchPosModules } from "./utils/posModules";
import LocationControlGate from "./components/desktop/LocationControlGate";
import CustomerScreenHost from "./components/customer-facing-screen/CustomerScreenHost";
import { TrainingModeContextProvider } from "./context/TrainingModeContext";

const AuthContextProvider = lazy(
  () => import("./components/contexts/AuthContext")
);
const AdminRoute = lazy(() => import("./components/routes/AdminRoute"));
const StoreContextProvider = lazy(
  () => import("./components/contexts/StoreContext")
);
const POSUserContextProvider = lazy(
  () => import("./components/contexts/POSUserContext")
);
const TrainingModeTimer = lazy(
  () => import("./components/ui/TrainingModeTimer")
);
const CustomerFacingScreen = lazy(
  () => import("./components/customer-facing-screen/CustomerFacingScreen")
);

const PublicRoute = lazy(() => import("./components/routes/PublicRoute"));
const OrderRoute = lazy(() => import("./components/routes/OrderRoute"));
const TimeZoneProvider = lazy(() => import("./components/contexts/TimeZoneContext"));
const KeyboardContextProvider = lazy(() => import("./components/contexts/KeyboardContext"));
const VirtualKeyboard = lazy(() => import("./components/keyboard/VirtualKeyboard.js"));
const KeyboardGlobalHandler = lazy(() => import("./components/keyboard/KeyboardGlobalHandler"));
const POSUserLoginRequired = lazy(() => import("./components/pos-user-login/POSUserLoginRequired"));
const TrayExitPin = lazy(() => import("./components/desktop/TrayExitPin"));
const DebugPage = lazy(() => import("./components/debug/DebugPage"));
const EmailReceiptBridge = lazy(() => import("./components/emailReceipt/EmailReceiptBridge"));
const PaymentStatusOverlay = lazy(() => import("./components/pos/PaymentStatusOverlay"));
const FullscreenProvider = lazy(() => import("./components/contexts/FullscreenContext").then(module => ({ default: module.FullscreenProvider })));

const RouteLoadingFallback: FC = () => {
  return (
    <div
      style={
        {
          height: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        } as any
      }
    >
      <div>Loading...</div>
    </div>
  );
};

const RenderRoute: FC = () => {
  const location = useLocation();
  const loadingFallbackNode = React.createElement(RouteLoadingFallback as any) as any;
  const hashPath = window.location.hash.replace(/^#/, "").split("?")[0];
  const trayExit = hashPath === "/tray-exit" || location.pathname === "/tray-exit";

  if (trayExit) {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(TrayExitPin as any)
    ) as any;
  }

  // Root path handler - redirect authenticated users immediately
  if (location.pathname === "/") {
    if (auth.currentUser && !auth.currentUser?.isAnonymous) {
      // User is logged in - the PIN opens on the dashboard. The kitchen screen is used when the dashboard is not available.
      window.location.hash = isTauriMode() ? "#/admin/pos" : "#/admin/setting";

      return <RouteLoadingFallback />;
    }
    // User not logged in - show login page via PublicRoute
  }

  // Modified condition to handle both /order and /table paths
  if (location.pathname.startsWith("/order") || (location.pathname.includes("/table") && !location.pathname.startsWith("/admin"))) {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(OrderRoute as any)
    ) as any;
  } else if (location.pathname === "/debug") {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(DebugPage as any)
    ) as any;
  } else if (
    location.pathname.includes("/admin") ||
    location.pathname === "/profile" ||
    location.pathname === "/email-verification" ||
    location.pathname === "/mfa-setup" ||
    location.pathname === "/setting"
  ) {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(
        StoreContextProvider as any,
        null,
        React.createElement(CustomerScreenHost as any),
        React.createElement(
          POSUserContextProvider as any,
          null,
          React.createElement(
            TrainingModeContextProvider as any,
            null,
            React.createElement(
              Suspense as any,
              { fallback: null },
              React.createElement(TrainingModeTimer as any)
            ),
            isWebMode() ? null : React.createElement(POSUserLoginRequired as any),
            React.createElement(LocationControlGate as any),
            React.createElement(
              Suspense as any,
              { fallback: null },
              React.createElement(EmailReceiptBridge as any)
            ),
            React.createElement(
              Suspense as any,
              { fallback: null },
              React.createElement(PaymentStatusOverlay as any)
            ),
            React.createElement(AdminRoute as any)
          )
        )
      )
    ) as any;
  } else if (location.pathname === "/customer") {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(CustomerFacingScreen as any)
    ) as any;
  } else {
    return React.createElement(
      Suspense as any,
      { fallback: loadingFallbackNode },
      React.createElement(PublicRoute as any)
    ) as any;
  }
};

const AppRoute: FC = () => {
  return (
    <>
      <RenderRoute />
    </>
  );
};

const AppShell: FC<{ children: React.ReactNode; bare?: boolean; fit?: boolean }> = ({ children, bare = false, fit = false }) => {
  return (
    <div className={fit ? "overflow-hidden" : bare ? "h-screen overflow-hidden" : "h-screen overflow-hidden pt-[var(--app-title-bar)]"}>
      {children}
    </div>
  );
};

const App: FC = () => {
  const location = useLocation();
  const customerScreen = location.pathname === "/customer";
  const hashPath = window.location.hash.replace(/^#/, "").split("?")[0];
  const trayExit = hashPath === "/tray-exit" || location.pathname === "/tray-exit";
  const { getBrandName, loading, oemConfig } = useOEM();

  // Update browser tab title with OEM brand name
  useEffect(() => {
    if (!loading) {
      const brandName = getBrandName('en');
      document.title = `${brandName} - POS System`;
    }
    if (!trayExit) initDesktopShell();
  }, [getBrandName, loading, oemConfig, trayExit]);

  useEffect(() => {
    if (trayExit) return undefined;
    applySavedUiScale();
    const stopPermanentTime = watchPermanentTime();
    const stopPosModules = watchPosModules();
    return () => {
      stopPermanentTime();
      stopPosModules();
    };
  }, [trayExit]);

  useEffect(() => {
    if (!isTauriMode() || process.env.NODE_ENV === "production") return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "F12") return;
      event.preventDefault();
      import("@tauri-apps/api/core")
        .then(({ invoke }) => invoke("toggle_devtools"))
        .catch(() => undefined);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const unusedChakraThemeRemoved = true;
  if (!unusedChakraThemeRemoved) {
    const _legacyTheme = {
    colors: {
      submit: {
        100: "#45f776",
        200: "#3fe06b",
        300: "#3ccf64",
        400: "#35b859",
        500: "#32a852",
        600: "#2b9447",
        700: "#23783a",
        800: "#1c612f",
        900: "#144521",
      },
      edit: {
        100: "#4058de",
        200: "#3b51cc",
        300: "#364aba",
        400: "#3244a8",
        500: "#2c3b91",
        600: "#243178",
        700: "#1c2661",
        800: "#151d4a",
        900: "#0e1330",
      },
      remove: {
        100: "#fa4646",
        200: "#ed4545",
        300: "#de4040",
        400: "#c73838",
        500: "#b53333",
        600: "#a12d2d",
        700: "#8f2828",
        800: "#782222",
        900: "#611a1a",
      },
      primary: {
        50: "#E6F0FA",
        100: "#C5DCFA",
        200: "#9DC2F0",
        300: "#73A8E6",
        400: "#5296E0",
        500: "#3182CE", // blue.500
        600: "#2B6CB0", // blue.600
        700: "#2C5282", // blue.700
        800: "#1E3B5E",
        900: "#162A40",
      },
      secondary: {
        50: "#E7F5EE",
        100: "#C4E6D3",
        200: "#9DD6B7",
        300: "#73C698",
        400: "#53B97F",
        500: "#38A169", // green.500
        600: "#2F855A", // green.600
        700: "#276749",
        800: "#1E5237",
        900: "#133929",
      },
      accent: {
        50: "#FDF0E6",
        100: "#FAD8C5",
        200: "#F6BE9F",
        300: "#F1A37A",
        400: "#EE8E5F",
        500: "#DD6B20", // orange.500
        600: "#C05621", // orange.600
        700: "#9C4221",
        800: "#7B341E",
        900: "#5C2819",
      },
      gray: {
        50: "#F7FAFC",
        100: "#EDF2F7",
        200: "#E2E8F0",
        300: "#CBD5E0",
        400: "#A0AEC0",
        500: "#718096",
        600: "#4A5568",
        700: "#2D3748",
        800: "#1A202C",
        900: "#171923",
      },
    },
    components: {
      Button: {
        baseStyle: {
          fontWeight: "medium",
          borderRadius: "md",
          _focus: {
            boxShadow: "none",
          },
        },
        variants: {
          solid: (props) => ({
            bg: `${props.colorScheme}.500`,
            color: "white",
            _hover: {
              bg: `${props.colorScheme}.600`,
              transform: "translateY(-2px)",
              boxShadow: "md",
            },
            _active: {
              bg: `${props.colorScheme}.700`,
              transform: "translateY(0)",
            },
            transition: "all 0.2s",
          }),
          outline: (props) => ({
            borderColor: `${props.colorScheme}.500`,
            color: `${props.colorScheme}.500`,
            _hover: {
              bg: `${props.colorScheme}.50`,
            },
            transition: "all 0.2s",
          }),
          ghost: (props) => ({
            color: `${props.colorScheme}.500`,
            _hover: {
              bg: `${props.colorScheme}.50`,
            },
            transition: "all 0.2s",
          }),
        },
      },
      IconButton: {
        baseStyle: {
          borderRadius: "md",
          _focus: {
            boxShadow: "none",
          },
        },
        variants: {
          solid: (props) => ({
            bg: `${props.colorScheme}.500`,
            color: "white",
            _hover: {
              bg: `${props.colorScheme}.600`,
              transform: "translateY(-2px)",
              boxShadow: "md",
            },
            _active: {
              bg: `${props.colorScheme}.700`,
              transform: "translateY(0)",
            },
            transition: "all 0.2s",
          }),
        },
      },
      Input: {
        baseStyle: {
          field: {
            borderRadius: "md",
          },
        },
        defaultProps: {
          focusBorderColor: "primary.500",
        },
        variants: {
          outline: {
            field: {
              _focus: {
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              },
            },
          },
        },
      },
      Textarea: {
        baseStyle: {
          borderRadius: "md",
        },
        defaultProps: {
          focusBorderColor: "primary.500",
        },
        variants: {
          outline: {
            _focus: {
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            },
          },
        },
      },
      Select: {
        baseStyle: {
          field: {
            borderRadius: "md",
          },
        },
        defaultProps: {
          focusBorderColor: "primary.500",
        },
        variants: {
          outline: {
            field: {
              _focus: {
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              },
            },
          },
        },
      },
      FormLabel: {
        baseStyle: {
          fontSize: "sm",
          fontWeight: "medium",
          color: "gray.700",
          marginBottom: "1",
        },
      },
      FormError: {
        baseStyle: {
          text: {
            fontSize: "xs",
            fontWeight: "medium",
          },
        },
      },
      Modal: {
        baseStyle: {
          dialog: {
            borderRadius: "lg",
            boxShadow: "xl",
          },
          header: {
            padding: "4",
            borderBottom: "1px solid",
            borderColor: "gray.100",
          },
          body: {
            padding: "4",
          },
          footer: {
            padding: "4",
            borderTop: "1px solid",
            borderColor: "gray.100",
          },
        },
      },
      Tabs: {
        variants: {
          line: {
            tab: {
              _selected: {
                color: "primary.600",
                borderColor: "primary.500",
              },
              _focus: {
                boxShadow: "none",
              },
            },
          },
        },
      },
      Card: {
        baseStyle: {
          container: {
            borderRadius: "lg",
            boxShadow: "sm",
            overflow: "hidden",
          },
          header: {
            padding: "4",
          },
          body: {
            padding: "4",
          },
          footer: {
            padding: "4",
            borderTop: "1px solid",
            borderColor: "gray.100",
          },
        },
      },
      Accordion: {
        baseStyle: {
          container: {
            borderRadius: "md",
            border: "1px solid",
            borderColor: "gray.200",
          },
          button: {
            _focus: {
              boxShadow: "none",
            },
          },
        },
      },
      Checkbox: {
        baseStyle: {
          control: {
            _focus: {
              boxShadow: "none",
              borderColor: "primary.500",
            },
          },
        },
        defaultProps: {
          colorScheme: "primary",
        },
      },
    },
    styles: {
      global: {
        body: {
          bg: "gray.50",
          color: "gray.800",
        },
        // Improved scrollbar styling
        "::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "::-webkit-scrollbar-track": {
          background: "rgba(240, 240, 240, 0.8)",
          borderRadius: "8px",
        },
        "::-webkit-scrollbar-thumb": {
          background: "rgba(100, 100, 100, 0.5)",
          borderRadius: "8px",
          border: "2px solid transparent",
          backgroundClip: "content-box",
        },
      },
    },
    };
  }

  // Initialize IndexedDB databases on app startup
  useEffect(() => {
    if (trayExit) return undefined;
    // Monitor online/offline status to manage processor
    const handleOnlineStatusChange = () => {
      if (connectionMonitor.getStatus()) {
        // Online - processor will automatically start processing
        console.log('[App] Online - print job processor active');
      } else {
        // Offline - jobs will be queued but not processed until online
        console.log('[App] Offline - print jobs will be queued');
      }
    };

    connectionMonitor.startFirebaseConnectionWatch();

    const initDatabases = async () => {
      let databasesInitialized = false;
      try {
        console.log('[App] Initializing local stores...');
        await cartDB.init();
        await offlineDB.init();
        await customerDB.init();
        await printJobQueue.init();
        console.log('[App] Local stores initialized');
        databasesInitialized = true;

        if (isTauriMode()) {
          try {
            const { initMevAPI } = await import('./utils/initMevAPI');
            await initMevAPI();
            console.log('[App] MEV API initialized successfully');
          } catch (mevError) {
            console.error('[App] Failed to initialize MEV API:', mevError);
          }
        }

        // Get selected store ID
        const selectedStoreId = localStorage.getItem('selectedStore');

        // BACKGROUND PRELOAD: Load master data in background (non-blocking)
        // AuthContext and StoreContext have their own caching mechanisms
        // This preload warms the cache but doesn't block app startup
        setTimeout(async () => {
          try {
            console.log('Offline Debug [App] Starting background master data preload...');
            const startTime = Date.now();
            await masterDataCache.preloadAll(selectedStoreId || undefined);
            const duration = Date.now() - startTime;
            console.log(`Offline Debug [App] ✓ Master data cache preloaded in ${duration}ms`);
          } catch (error) {
            console.error('Offline Debug [App] ✗ Failed to preload master data cache:', error);
          }
        }, 0); // Start immediately but don't block

        // Detect environment
        const isElectron = window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.protocol === 'file:';

        if (!isElectron) {
          console.log('[App] Web mode - data loaded directly from Firestore');
        }

        // Initialize certificates (load private key into secure memory)
        if (selectedStoreId) {
          try {
            const envirn = localStorage.getItem(`cloudMevEnvirn_${selectedStoreId}`) || localStorage.getItem('envirn') || 'DEV';
            // Determine environment for certificate initialization
            // Note: In some parts of the app 'envirn' is used, in others 'globalEnvirn'
            // We use the same logic as mevOrderJobManager to be consistent

            console.log('[App] Initializing certificates for store:', selectedStoreId, 'Env:', envirn);
            const { bindEnrolledCertificate } = await import('./cloud-mev/installedCertificate');
            await bindEnrolledCertificate(selectedStoreId, envirn);
            const { initializeCertificates } = await import('./utils/certificates/certificateInitializer');

            // Don't await this if we want it to happen in background, 
            // but user asked for "retrieve and cache... when application starts"
            // Since it's async and might take a moment, and we want it ready, awaiting is safer
            // but catching errors so it doesn't block the app
            await initializeCertificates(selectedStoreId, envirn.toLowerCase());
          } catch (certError) {
            console.error('[App] Failed to initialize certificates:', certError);
          }
        }
      } catch (error) {
        console.error('[App] Failed to initialize databases:', error);
      }

      // Initialize print job processor only if databases were initialized successfully
      if (databasesInitialized) {
        try {
          jobProcessor.start();
          console.log('[App] Print job processor started');

          // Listen to connection changes
          window.addEventListener('online', handleOnlineStatusChange);
          window.addEventListener('offline', handleOnlineStatusChange);
        } catch (error) {
          console.error('[App] Failed to initialize print job processor:', error);
        }
      } else {
        console.warn('[App] Print job processor not started - databases failed to initialize');
      }
    };

    initDatabases();

    // Cleanup on unmount
    return () => {
      jobProcessor.stop();
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [trayExit]);

  const rootFallback: React.ReactElement = (
    <div
      style={
        {
          height: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        } as any
      }
    >
      <div>Loading...</div>
    </div>
  );

  return (
    <TooltipProvider>
      <Toaster position="top-center" richColors closeButton />
      <Suspense
        fallback={rootFallback}
      >
        <FullscreenProvider>
          <AuthContextProvider>
            <Suspense fallback={rootFallback}>
              <TimeZoneProvider>
                <KeyboardContextProvider>
                  <ColorModeSync />
                  {!customerScreen && !trayExit && !isWebMode() && <CustomTitleBar />}
                  {!customerScreen && !trayExit && <OfflineBanner />}
                  <AppShell bare={customerScreen || trayExit} fit={trayExit}>
                    <AppRoute />
                  </AppShell>
                  <Suspense fallback={null}>
                    {!trayExit && !isWebMode() && <KeyboardGlobalHandler />}
                    {!trayExit && !isWebMode() && <VirtualKeyboard />}
                  </Suspense>
                </KeyboardContextProvider>
              </TimeZoneProvider>
            </Suspense>
          </AuthContextProvider>
        </FullscreenProvider>
      </Suspense>
    </TooltipProvider>
  );
};

export default App;